const NUMBER_WORDS = {
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  twentyfour: 24,
  fortyeight: 48,
};

function wordsToNumber(s) {
  const k = String(s).toLowerCase().trim();
  if (/^\d+$/.test(k)) return parseInt(k, 10);
  if (NUMBER_WORDS[k] !== undefined) return NUMBER_WORDS[k];
  if (NUMBER_WORDS[k.replace(/[-\s]/g, "")] !== undefined) {
    return NUMBER_WORDS[k.replace(/[-\s]/g, "")];
  }
  return null;
}

const NUM =
  "(\\d+|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|twenty[-\\s]?four|forty[-\\s]?eight)";
const UNIT = "(hours?|hrs?|business[\\s-]+days?|calendar[\\s-]+days?|days?|weeks?)";
const FILLER = "(?:[^.]{0,80}?)";
const ANCHOR =
  '(in\\s+advance\\s+of\\s+[^.]*?arrival|prior\\s+to\\s+(?:the\\s+)?(?:desired|intended|anticipated)?\\s*arrival|prior\\s+to\\s+"?(?:wheels\\s+up|departure)"?\\s+from\\s+(?:the\\s+)?foreign|prior\\s+to\\s+landing)';
const NUM_TO_UNIT = "[\\s-]*(?:\\(\\s*\\d+\\s*\\)[\\s-]*)?";
const CLAUSE_RE = new RegExp(`${NUM}${NUM_TO_UNIT}${UNIT}\\s*${FILLER}\\s*${ANCHOR}`, "gi");
const MAX_PREFIX_RE = /(not\s+more\s+than|no\s+more\s+than|within)\s*(?:[a-z,\s]{0,15})?$/i;
const INNER_DURATION_RE =
  /\b(\d+|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|twenty[-\s]?four|forty[-\s]?eight)[\s-]*(hours?|hrs?|business[\s-]+days?|calendar[\s-]+days?|days?|weeks?)\b/i;
const FILLER_CEILING_RE = /\b(not\s+more\s+than|no\s+more\s+than|within)\b/i;
const CLOCK_DEADLINE_RE = /\b(?:prior\s+to|no\s+later\s+than|by)\s+\d{3,4}\b/i;
// Tiered numeric range ("24-72 hours"): conditions unknown → caller must show
// the yellow "check requirements" banner instead of picking an endpoint.
const NUMERIC_RANGE_RE = /\b\d+\s*(?:[-–—]|to|through)\s*\d+\s*(?:hours?|hrs?|days?|weeks?)\b/i;
// KLAL-style clock deadline on the calendar day before arrival; not a duration.
const _TZ_ALT = "E[SD]?T|C[SD]?T|M[SD]?T|P[SD]?T|AK[SD]?T|HS?T|AS?T|AD?T|UTC|GMT|ZULU|Z";
const PRIOR_DAY_CLOCK_RE = new RegExp(
  `no\\s+later\\s+than\\s+(\\d{3,4})\\s*(?:hours?|hrs?)?\\s*(?:(${_TZ_ALT})\\s+)?(?:on\\s+)?the\\s+day\\s+prior\\s+(?:to|of)\\s+(?:the\\s+)?(?:desired|intended|anticipated)?\\s*arrival`,
  "i"
);

function unitKey(unitRaw) {
  const u = unitRaw.toLowerCase().replace(/[-\s]+/g, " ");
  if (u.startsWith("hr") || u.startsWith("hour")) return "h";
  if (u.startsWith("business")) return "bday";
  if (u.startsWith("calendar") || u === "day" || u === "days") return "day";
  if (u.startsWith("week")) return "week";
  return null;
}

function anchorRef(anchorRaw) {
  const a = anchorRaw.toLowerCase();
  if (a.includes("departure") || a.includes("wheels")) return "departure";
  if (a.includes("arrival") || a.includes("landing")) return "arrival";
  return null;
}

export function parsePtl(raw) {
  if (!raw) return null;
  const s = String(raw);

  const pd = s.match(PRIOR_DAY_CLOCK_RE);
  if (pd) {
    return {
      qty: parseInt(pd[1], 10),
      unit: "clock_prior_day",
      ref: "arrival",
      tz: pd[2] ? pd[2].toUpperCase() : null,
    };
  }

  if (NUMERIC_RANGE_RE.test(s)) return null;

  const found = [];
  let sawTieredClause = false;
  let m;
  CLAUSE_RE.lastIndex = 0;
  while ((m = CLAUSE_RE.exec(s)) !== null) {
    const qty = wordsToNumber(m[1]);
    const unit = unitKey(m[2]);
    const ref = anchorRef(m[3]);
    if (!qty || !unit || !ref) continue;
    const preceding = s.slice(Math.max(0, m.index - 25), m.index);
    if (MAX_PREFIX_RE.test(preceding)) continue;
    const unitEnd = m.index + m[0].indexOf(m[2]) + m[2].length;
    const anchorStart = m.index + m[0].lastIndexOf(m[3]);
    const filler = s.slice(unitEnd, anchorStart);
    if (INNER_DURATION_RE.test(filler) && !FILLER_CEILING_RE.test(filler)) {
      sawTieredClause = true;
      continue;
    }
    found.push({ qty, unit, ref });
  }
  if (sawTieredClause) return null;

  const leadSection = s.split(/permission\s+to\s+land\s+is\s+granted/i)[0];
  const hasClockDeadline = CLOCK_DEADLINE_RE.test(leadSection);
  if (hasClockDeadline) return null;

  if (found.length === 1) return found[0];
  if (found.length > 1) {
    const key = (x) => `${x.qty}|${x.unit}|${x.ref}`;
    const uniq = [...new Map(found.map((x) => [key(x), x])).values()];
    if (uniq.length === 1) return uniq[0];
    return null;
  }

  if (/prior\s+to\s+departure\s+from\s+(?:the\s+)?foreign/i.test(s)) {
    if (
      /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|twenty[-\s]?four|forty[-\s]?eight|an)\s*[-\s]\s*(hours?|hrs?|days?|weeks?)\b/i.test(
        s
      )
    ) {
      return null;
    }
    return { qty: null, unit: null, ref: "departure" };
  }

  return null;
}

export function formatPtl(parsed, t) {
  if (!parsed) return null;
  if (parsed.qty === null) return t("cbp.ptlParsed.noMin");
  if (parsed.unit === "clock_prior_day") {
    const hhmm = String(parsed.qty).padStart(4, "0");
    const time = `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
    const tzPart = parsed.tz ? ` ${parsed.tz}` : "";
    return t("cbp.ptlParsed.priorDayDeadline", { time, tz: tzPart }).replace(/\s+/g, " ").trim();
  }
  const refStr =
    parsed.ref === "arrival"
      ? t("cbp.ptlParsed.beforeArrival")
      : t("cbp.ptlParsed.beforeDeparture");
  const plural = parsed.qty !== 1;
  let unitStr;
  switch (parsed.unit) {
    case "h":
      unitStr = t("cbp.ptlParsed.hour");
      break;
    case "bday":
      unitStr = t(plural ? "cbp.ptlParsed.businessDays" : "cbp.ptlParsed.businessDay");
      break;
    case "day":
      unitStr = t(plural ? "cbp.ptlParsed.days" : "cbp.ptlParsed.day");
      break;
    case "week":
      unitStr = t(plural ? "cbp.ptlParsed.weeks" : "cbp.ptlParsed.week");
      break;
    default:
      unitStr = "";
  }
  return `≥ ${parsed.qty} ${unitStr} ${refStr}`;
}
